import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import type { CanvasNote, Section, Task } from '@/src/renderer/lib/db/models'
import type { CanvasApplyApi } from '../components/ProjectCanvas'
import { noteToTaskFields } from '../components/ProjectCanvas'
import { computeAppendOrders } from '../utils/appendTaskOrders'

export interface UseApplyCanvasNotesOptions {
  /** `undefined`, solange die Route noch keine Kennung hergibt — dann tut die Übernahme nichts. */
  projectId: string | undefined
  lifeSpace: 'personal' | 'work' | 'home'
  /** Der Zielabschnitt: der zuletzt benutzte, `null` für den Block oben. */
  targetSectionId: string | null
  tasks: Task[] | undefined
  sections: Section[] | undefined
  /**
   * Zeigt Aufgaben in der Liste: auswählen und hinscrollen.
   *
   * `openList` holt dabei eine **maximiert** weggefahrene Aufgabenliste zurück.
   * Die Übernahme selbst ruft ohne — sie zieht niemandem die maximierte Fläche
   * unter den Händen weg; der Knopf im Hinweis ruft mit.
   */
  revealTasks: (taskIds: string[], options?: { openList?: boolean }) => void
}

/**
 * **Übernehmen** — aus Canvas-Elementen werden Aufgaben.
 *
 * Sie läuft hier und nicht im Canvas, weil nur die Seite weiß, WOHIN eine
 * Aufgabe gehört: `lifeSpace`, die Abschnitte und der zuletzt benutzte liegen
 * hier. Der Canvas steuert über {@link CanvasApplyApi} zwei Dinge bei, die
 * niemand sonst hat — den Stand der offenen Schreibvorgänge und den
 * Rückgängig-Stapel.
 *
 * Siehe CONTEXT.md („Übernehmen", „Verbindung") und ADR 0011.
 */
export function useApplyCanvasNotes({
  projectId, lifeSpace, targetSectionId, tasks, sections, revealTasks,
}: UseApplyCanvasNotesOptions) {
  const { t } = useTranslation()
  const source = useDataSource()

  return useCallback(async (notes: CanvasNote[], api: CanvasApplyApi | null) => {
    if (!projectId || notes.length === 0) return

    /*
      Erst abwarten, was ein offener **Bearbeiten-Modus** gerade schreibt.

      Der Text eines Zettels geht erst beim Verlassen des Feldes in die
      Datenbank, und zwar asynchron. Der Klick auf den Knopf nimmt dem Feld
      zwar den Fokus, läuft aber weiter, während der Schreibvorgang unterwegs
      ist — ohne dieses Warten bekäme die Aufgabe den ALTEN Text, und der
      Zettel zeigte sofort „bearbeitet".
    */
    await api?.flushPendingWrites()

    // Danach frisch lesen: Was oben eingetroffen ist, steht in `notes` noch
    // nicht — die Live-Query meldet es erst später.
    const fresh = (await source.getCanvasNotesByIds(notes.map(n => n.id)))
      .filter((note): note is CanvasNote => !!note && !note.deletedAt && note.taskId === null && note.kind !== 'area' && note.kind !== 'text')
    if (fresh.length === 0) return

    const prepared = fresh.flatMap(note => {
      const fields = noteToTaskFields(note)
      return fields ? [{ note, fields }] : []
    })
    if (prepared.length === 0) return

    const orders = computeAppendOrders({
      tasks: tasks ?? [],
      sections: sections ?? [],
      sectionId: targetSectionId,
      count: prepared.length,
    })

    const convertedAt = new Date()
    const createdTaskIds: string[] = []
    const changes: Parameters<CanvasApplyApi['recordApply']>[0] = []

    for (const [index, { note, fields }] of prepared.entries()) {
      const task = await source.createTask({
        title: fields.title,
        description: fields.description,
        status: 'todo',
        attention: null,
        category: 'other',
        lifeSpace,
        projectId,
        sectionId: targetSectionId,
        order: orders[index]!,
        pausedAt: null,
        dueDate: null,
        dueTime: null,
        duePeriod: null,
        completedAt: null,
        estimatedDuration: null,
        actualDuration: null,
        tags: [],
        rewardId: null,
        parentTaskId: null,
        durationMode: null,
        startDate: null,
        startTime: null,
        startPeriod: null,
      })

      createdTaskIds.push(task.id)

      // Der Text von JETZT wird festgehalten — daran erkennt der Zettel
      // später, ob er sich seither geändert hat.
      const after = { taskId: task.id, convertedAt, convertedContent: note.content }
      await source.updateCanvasNote(note.id, after)
      changes.push({
        id: note.id,
        before: { taskId: null, convertedAt: null, convertedContent: null },
        after,
      })
    }

    // EIN Schritt für die ganze Geste, samt der Aufgaben (ADR 0011): ⌘Z klappt
    // die Ecken wieder auf UND nimmt die Aufgaben mit.
    api?.recordApply(changes, createdTaskIds)

    // Hinsehen, ohne dass man erst klicken muss: Die Liste rückt zur frisch
    // angelegten Aufgabe und wählt sie aus. Bei maximiertem Canvas passiert
    // hier nichts — dort ist die Liste gar nicht im Bild (siehe `revealTasks`).
    revealTasks(createdTaskIds)

    /*
      Der Hinweis führt HIN, er nimmt nicht zurück.

      Nach einer Übernahme will man in aller Regel hinsehen — man hat den Knopf
      ja absichtlich gedrückt. Das Zurücknehmen ist ⌘Z; zwei Rückwege zur
      selben Sache bräuchten einen Wächter und stellten die seltene Handlung
      vor die häufige.

      Überhaupt ein Hinweis, obwohl der Canvas sonst ohne auskommt: Die Wirkung
      liegt in der **Aufgabenliste** und ist bei maximiertem Canvas gar nicht
      im Bild (CONTEXT.md, „Übernehmen").
    */
    showSuccessToast({
      message: t('projectCanvas.applyTasks.toast', { count: createdTaskIds.length }),
      action: {
        label: t('common.show'),
        // Mit `openList`: Wer hier drückt, will zur Aufgabe — auch aus dem
        // maximierten Canvas heraus.
        onClick: () => revealTasks(createdTaskIds, { openList: true }),
      },
    })
  }, [projectId, lifeSpace, targetSectionId, tasks, sections, revealTasks, t, source])
}
